import { useMemo } from 'react';

import { EventTotalCostTooltip } from '../event-total-cost-tooltip';
import { EventDiscount } from './event-discount';

import { UsdcIcon } from '@/components/common/icons/usdc';
import { useFetchGlobalState } from '@/hooks/solana/query/use-fetch-global-state';
import { useFetchMarketState } from '@/hooks/solana/query/use-fetch-market-state';
import { useFetchUserState } from '@/hooks/solana/query/use-fetch-user-state';
import { useAuthenticatedWallet } from '@/hooks/ui/use-authenticated-wallet';
import { getLiquidityIncentiveFeeDiscount } from '@/lib/utils';
import { useOrderbookStore } from '@/store/orderbook';

type Props = {
  marketId: number;
  receiveAmountWithoutFee: string | number;
  price: string | number | null; // NULL FOR MARKET ORDERS
  nextBestPrice: number | null;
  matchingAmount: number;
  tradeType: 'buy' | 'sell';
  avgPrice: number | string;
};

export const EventSellReceive = ({
  marketId,
  receiveAmountWithoutFee,
  price,
  matchingAmount,
  nextBestPrice,
  tradeType,
  avgPrice,
}: Props) => {
  const { setSolAmount } = useOrderbookStore();
  const { publicKey } = useAuthenticatedWallet();

  const { data: marketState } = useFetchMarketState(marketId);

  const { data: globalState } = useFetchGlobalState();
  const { data: userState } = useFetchUserState(publicKey);

  const fee = useMemo(() => {
    if (!globalState)
      return {
        marketFee: 0,
        limitFee: 0,
        liquidityIncentiveFeeDiscount: 0,
        spread: 0,
      };

    const tradeFee = Number(marketState?.tradeFeePercent ?? 0) / 10000;
    const referralFee = userState?.referrer
      ? Number(globalState.referralFeeDiscount) / 10000
      : 0;

    const discount =
      Number(globalState.liquidityIncentiveDiscountPerSpread) / 10000;
    const maxSpread = Number(globalState.liquidityIncentiveMaxSpread);

    return getLiquidityIncentiveFeeDiscount(
      tradeFee,
      referralFee,
      discount,
      maxSpread,
      price,
      nextBestPrice,
    );
  }, [
    globalState,
    marketState?.tradeFeePercent,
    nextBestPrice,
    price,
    userState?.referrer,
  ]);

  const cost = useMemo(() => {
    const marketAmount = matchingAmount;
    const limitAmount = +receiveAmountWithoutFee - marketAmount;
    const marketFee = marketAmount * fee.marketFee;
    const limitFee = limitAmount * fee.limitFee;
    setSolAmount(0);
    return {
      amount: +receiveAmountWithoutFee,
      amountWithFee: marketAmount - marketFee + (limitAmount - limitFee),
      feeAmount: marketFee + limitFee,
    };
  }, [
    fee.limitFee,
    fee.marketFee,
    matchingAmount,
    receiveAmountWithoutFee,
    setSolAmount,
  ]);

  return (
    <div className="flex flex-row text-[20px] font-medium sm:text-[24px]">
      <div className="flex grow items-center">
        <p>You'll receive</p>
        <EventDiscount
          marketId={marketId}
          nextBestPrice={nextBestPrice}
          isMarketLimit={
            matchingAmount > 0 && +receiveAmountWithoutFee > matchingAmount
          }
          discount={
            +receiveAmountWithoutFee - matchingAmount > 0
              ? fee.liquidityIncentiveFeeDiscount
              : 0
          }
          spread={fee.spread}
          tradeType={tradeType}
        />
      </div>
      <EventTotalCostTooltip cost={{ ...cost, avgPrice }}>
        <div className="flex flex-row items-center justify-center gap-[10px] text-[20px] font-medium sm:text-[24px]">
          {cost.amountWithFee.toFixed(2)}
          <UsdcIcon className="size-[24px] sm:size-[32px]" />
        </div>
      </EventTotalCostTooltip>
    </div>
  );
};
